import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { FiPackage, FiRefreshCw, FiX } from 'react-icons/fi';
import { useShopping } from '../context/ShoppingContext';
import { useToast } from '../context/ToastContext';
import '../styles/Orders.css';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [returnReason, setReturnReason] = useState('');
  const [returnType, setReturnType] = useState('return');
  const [videoFile, setVideoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [cancellingOrderIds, setCancellingOrderIds] = useState(new Set());
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [showAddressOrderId, setShowAddressOrderId] = useState(null);
  const [refundBreakdown, setRefundBreakdown] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundTrackingData, setRefundTrackingData] = useState(null);
  const [refundTrackingLoading, setRefundTrackingLoading] = useState(false);
  const { user, authLoading, addToCart } = useShopping();
  const navigate = useNavigate();
  const toast = useToast();

  // Return shipping charge (can be configured)
  const RETURN_SHIPPING_CHARGE = 100; // ₹100 return shipping charge

  // Check if reason qualifies for full refund (no shipping deduction)
  const isFullRefundReason = (reason) => {
    return ['damaged', 'wrong_item'].includes(reason);
  };

  // Check if reason requires video proof
  const requiresVideoProof = (reason) => {
    return ['damaged', 'wrong_item'].includes(reason);
  };

  // Calculate refund breakdown when reason changes
  useEffect(() => {
    if (selectedOrder && returnReason) {
      const orderTotal = selectedOrder.totalPrice || 0;
      if (isFullRefundReason(returnReason)) {
        setRefundBreakdown({
          orderTotal,
          shippingDeduction: 0,
          refundAmount: orderTotal,
          isFullRefund: true,
          message: 'Full refund - No shipping charge deducted for damaged/wrong items'
        });
      } else {
        const refundAmount = Math.max(0, orderTotal - RETURN_SHIPPING_CHARGE);
        setRefundBreakdown({
          orderTotal,
          shippingDeduction: RETURN_SHIPPING_CHARGE,
          refundAmount,
          isFullRefund: false,
          message: `Return shipping charge of ₹${RETURN_SHIPPING_CHARGE} will be deducted`
        });
      }
    } else {
      setRefundBreakdown(null);
    }
  }, [selectedOrder, returnReason]);

  useEffect(() => {
    // Wait for auth state to be determined before redirecting
    if (authLoading) return;

    if (!user) {
      navigate('/account');
      return;
    }
    fetchOrders();

    // Smart auto-refresh for Quick orders
    // Poll every 30s when there are active Quick orders, 60s otherwise
    const getPollingInterval = () => {
      const hasLiveQuickOrder = orders.some(
        order => order.deliveryType === 'quick' &&
          order.tracking?.isLive &&
          !['delivered', 'cancelled'].includes(order.status)
      );
      return hasLiveQuickOrder ? 30000 : 60000; // 30s for live Quick, 60s otherwise
    };

    let interval;
    const startPolling = () => {
      interval = setTimeout(() => {
        fetchOrders();
        startPolling(); // Re-schedule with potentially updated interval
      }, getPollingInterval());
    };
    startPolling();

    return () => clearTimeout(interval);
  }, [user, authLoading, navigate, orders.length]); // Re-run when orders count changes

  const fetchOrders = async () => {
    try {
      // Get fresh Firebase ID token from the user object
      if (!user) return;
      const token = await user.getIdToken(); // Use cached token if valid

      const response = await apiClient.get('/orders', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setOrders(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setLoading(false);
    }
  };

  const handleReturnClick = (order) => {
    setSelectedOrder(order);
    setShowReturnModal(true);
    setReturnReason('');
    setReturnType('return');
    setVideoFile(null);
    setRefundBreakdown(null);
  };

  // Handle Buy Again - add item to cart and navigate
  const handleBuyAgain = async (item) => {
    try {
      await addToCart({
        id: item.id || item._id || item.productId,
        name: item.name,
        price: item.price,
        image: item.image,
        quantity: 1
      });
      navigate('/cart');
    } catch (error) {
      console.error('Error adding to cart:', error);
      const productId = item.id || item._id || item.productId;
      if (productId) {
        navigate(`/product/${productId}`);
      }
    }
  };

  // Navigate to product page
  const handleProductClick = (item) => {
    const productId = item.id || item._id || item.productId;
    if (productId) {
      navigate(`/product/${productId}`);
    }
  };

  // Toggle order details
  const toggleOrderDetails = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  // Get status display info
  const getStatusDisplay = (order) => {
    const status = order.status;
    switch (status) {
      case 'delivered':
        return {
          title: `Delivered ${new Date(order.updatedAt || order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long' })}`,
          subtitle: 'Package was handed to resident',
          className: 'delivered'
        };
      case 'shipped':
        return {
          title: 'Shipped',
          subtitle: 'Your package is on the way',
          className: 'shipped'
        };
      case 'processing':
        return {
          title: 'Order Confirmed',
          subtitle: 'Your order is being prepared for shipment',
          className: 'processing'
        };
      case 'pending':
        return {
          title: 'Order Placed',
          subtitle: 'Payment confirmed, preparing your order',
          className: 'pending'
        };
      case 'cancellation_pending':
        return {
          title: 'Cancellation Pending',
          subtitle: order.refundDetails?.refundId
            ? `Refund initiated - ₹${order.refundDetails.refundAmount || order.totalPrice} will be credited once cancellation is confirmed`
            : 'Waiting for courier to confirm cancellation',
          className: 'cancellation-pending'
        };
      case 'cancelled':
        return {
          title: 'Cancelled',
          subtitle: order.refundDetails?.refundId
            ? `Refund initiated - ₹${order.refundDetails.refundAmount || order.totalPrice} will be credited within 5-7 days`
            : 'This order has been cancelled',
          className: 'cancelled'
        };
      case 'return_requested':
        const returnStatus = order.returnDetails?.status;
        if (returnStatus === 'pending_admin_approval') {
          return {
            title: 'Return Under Review',
            subtitle: 'Your return request is pending admin approval',
            className: ''
          };
        } else if (returnStatus === 'pending_pickup') {
          return {
            title: 'Return Approved - Awaiting Pickup',
            subtitle: 'Courier will pick up the product soon. Refund will be processed after pickup.',
            className: ''
          };
        }
        return {
          title: 'Return Requested',
          subtitle: 'Your return is being processed',
          className: ''
        };
      case 'return_approved':
        return {
          title: 'Return Approved - Awaiting Pickup',
          subtitle: 'Courier will pick up the product soon. Refund will be processed after pickup confirmation.',
          className: ''
        };
      case 'return_pickup_confirmed':
        return {
          title: 'Pickup Confirmed - Refund Processing',
          subtitle: 'Product has been picked up. Refund is being processed.',
          className: ''
        };
      case 'refunded':
        return {
          title: 'Refunded',
          subtitle: order.refundDetails?.refundAmount
            ? `₹${order.refundDetails.refundAmount} has been refunded to your account`
            : 'Your refund has been issued',
          className: 'refunded'
        };
      default:
        return {
          title: status.charAt(0).toUpperCase() + status.slice(1),
          subtitle: '',
          className: ''
        };
    }
  };

  // Check if order can be cancelled
  const canCancelOrder = (order) => {
    return ['pending', 'processing'].includes(order.status);
  };

  // Handle cancel order
  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order? This action cannot be undone.')) {
      return;
    }

    // Immediately add to cancelling set to prevent double-clicks
    if (cancellingOrderIds.has(orderId)) {
      return; // Already cancelling this order
    }

    setCancellingOrderIds(prev => new Set([...prev, orderId]));
    try {
      const token = await user.getIdToken();
      const response = await apiClient.post(`/orders/${orderId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const isPending = response.data?.pendingConfirmation;
      const newStatus = isPending ? 'cancellation_pending' : 'cancelled';

      // Immediately update local state to reflect cancellation status
      setOrders(prevOrders => prevOrders.map(order => {
        if (order.id === orderId) {
          return {
            ...order,
            status: newStatus,
            refundDetails: response.data?.order?.refundDetails || response.data?.refund
          };
        }
        return order;
      }));

      // Show appropriate toast message
      if (isPending) {
        toast.info('Cancellation requested! Refund will be processed once the courier confirms cancellation.');
      } else {
        const refundInfo = response.data?.refund;
        if (refundInfo?.success) {
          toast.success('Order cancelled! Refund will be credited within 5-7 business days.');
        } else {
          toast.success('Order cancelled successfully.');
        }
      }

      // Refresh orders in background to get full details
      fetchOrders();
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to cancel order. Please try again.');
    } finally {
      // Remove from cancelling set
      setCancellingOrderIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  // Handle get support
  const handleGetSupport = (order) => {
    const subject = encodeURIComponent(`Support Request - Order #${order.orderId}`);
    const body = encodeURIComponent(`Hi,\n\nI need help with my order:\n\nOrder ID: ${order.orderId}\nOrder Date: ${new Date(order.createdAt).toLocaleDateString()}\nTotal: ₹${order.totalPrice}\n\nPlease describe your issue here:\n\n`);
    window.location.href = `mailto:support@kalingastore.shop?subject=${subject}&body=${body}`;
  };

  // Handle write review - navigate to product page with review section
  const handleWriteReview = (item) => {
    const productId = item.id || item._id || item.productId;
    if (productId) {
      navigate(`/product/${productId}?review=true`);
    }
  };

  // Handle track order - fetch tracking timeline and show modal
  const handleTrackOrder = async (order) => {
    setTrackingLoading(true);
    setShowTrackingModal(true);
    setTrackingData(null);

    try {
      const token = await user.getIdToken();
      const response = await apiClient.get(`/orders/${order.id}/track`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrackingData(response.data);
    } catch (error) {
      console.error('Error fetching tracking:', error);
      // Create a basic timeline from order status
      setTrackingData({
        order,
        timeline: [
          {
            status: 'ordered',
            title: 'Order Placed',
            description: 'Your order has been placed successfully',
            date: order.createdAt,
            completed: true
          },
          {
            status: order.status === 'cancelled' ? 'cancelled' : 'processing',
            title: order.status === 'cancelled' ? 'Cancelled' : 'Processing',
            description: order.status === 'cancelled' ? 'Order was cancelled' : 'Order is being processed',
            date: order.updatedAt,
            completed: true,
            isCancelled: order.status === 'cancelled'
          }
        ]
      });
    } finally {
      setTrackingLoading(false);
    }
  };

  // Handle refund status - fetch refund timeline from Razorpay and show modal
  const handleRefundStatus = async (order) => {
    setRefundTrackingLoading(true);
    setShowRefundModal(true);
    setRefundTrackingData(null);

    try {
      const token = await user.getIdToken();
      const response = await apiClient.get(`/orders/${order.id}/refund-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRefundTrackingData(response.data);
    } catch (error) {
      console.error('Error fetching refund status:', error);
      // Create a basic timeline from order's refund details
      const refundAmount = order.refundDetails?.refundAmount || order.totalPrice;
      const refundStatus = order.refundDetails?.refundStatus || 'initiated';
      const isRefundProcessed = refundStatus === 'processed';

      setRefundTrackingData({
        order: { orderId: order.orderId, totalPrice: order.totalPrice },
        refund: {
          amount: refundAmount,
          status: refundStatus,
          initiatedAt: order.refundDetails?.refundedAt || order.updatedAt
        },
        timeline: [
          {
            status: 'initiated',
            title: 'Refund Initiated',
            description: `Refund of ₹${refundAmount} has been initiated`,
            date: order.refundDetails?.refundedAt || order.updatedAt,
            completed: true
          },
          {
            status: 'processing',
            title: 'Refund Processing',
            description: 'Takes 3-5 working days',
            date: order.refundDetails?.refundedAt || order.updatedAt,
            completed: true
          },
          {
            status: 'processed',
            title: 'Refund Processed',
            description: 'Refund processed by Razorpay. Final credit depends on customer\'s bank (2–5 working days)',
            date: isRefundProcessed ? order.updatedAt : null,
            completed: isRefundProcessed
          }
        ]
      });
    } finally {
      setRefundTrackingLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        toast.warning('File size too large. Max 50MB allowed.');
        return;
      }
      setVideoFile(file);
    }
  };

  const handleSubmitReturn = async (e) => {
    e.preventDefault();

    // Validate video upload for damaged/wrong item claims
    if (requiresVideoProof(returnReason) && !videoFile) {
      toast.warning('Please upload an unboxing video as proof for damaged/wrong item claims.');
      return;
    }

    if (!returnReason) {
      toast.warning('Please select a reason for return.');
      return;
    }

    setUploading(true);
    try {
      const token = await user.getIdToken();
      let videoUrl = null;

      // Only upload video if required (damaged/wrong item)
      if (requiresVideoProof(returnReason) && videoFile) {
        const formData = new FormData();
        formData.append('video', videoFile);

        const uploadResponse = await apiClient.post('/uploads/return-proof', formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        videoUrl = uploadResponse.data.url;
      }

      // Submit Return Request with refund breakdown
      await apiClient.post(`/orders/${selectedOrder.id}/return`, {
        reason: returnReason,
        type: returnType,
        videoUrl,
        refundBreakdown: {
          orderTotal: refundBreakdown?.orderTotal || selectedOrder.totalPrice,
          shippingDeduction: refundBreakdown?.shippingDeduction || 0,
          refundAmount: refundBreakdown?.refundAmount || selectedOrder.totalPrice,
          isFullRefund: refundBreakdown?.isFullRefund || false
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const isFullRefundCase = refundBreakdown?.isFullRefund || false;
      const refundMsg = isFullRefundCase
        ? `Full refund of ₹${refundBreakdown?.refundAmount} will be processed after:\n1. Admin approval\n2. Courier pickup confirmation`
        : `Refund of ₹${refundBreakdown?.refundAmount} (after ₹${RETURN_SHIPPING_CHARGE} shipping deduction) will be processed after courier pickup confirmation.\n\nNo admin approval required for this reason.`;

      toast.success('Return request submitted successfully! You will receive an email notification at each step.');
      setShowReturnModal(false);
      fetchOrders();
    } catch (error) {
      console.error('Error submitting return:', error);
      toast.error(error.response?.data?.error || 'Failed to submit return request. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Show loading while auth state is being determined or orders are loading
  if (authLoading || loading) {
    return (
      <div className="orders-page">
        <div className="orders-container loading-orders">
          <FiPackage size={48} style={{ marginBottom: '1rem', color: '#ccc' }} />
          <p>Loading your orders...</p>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="orders-page">
        <div className="orders-container">
          <h1>Your Orders</h1>
          <div className="empty-orders">
            <FiPackage size={80} color="#ccc" style={{ marginBottom: '1rem' }} />
            <h2>You haven't placed any orders yet!</h2>
            <p>Start shopping to see your orders here.</p>
            <Link to="/products" className="btn-start-shopping">
              Start Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <div className="orders-container">
        <h1>Your Orders</h1>

        {orders.map((order) => {
          const statusInfo = getStatusDisplay(order);

          return (
            <div key={order.id} className="order-card">
              {/* Order Header - Compact Gray Bar */}
              <div className="order-header-info">
                <div className="order-header-left">
                  <div className="header-column">
                    <span className="header-label">ORDER PLACED</span>
                    <span className="header-value">
                      {new Date(order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="header-column">
                    <span className="header-label">TOTAL</span>
                    <span className="header-value">₹{order.totalPrice?.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="header-column ship-to-column">
                    <span className="header-label">SHIP TO</span>
                    <span
                      className="header-value ship-to"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAddressOrderId(showAddressOrderId === order.id ? null : order.id);
                      }}
                    >
                      {order.shippingAddress?.fullName || 'N/A'} ▾
                    </span>
                    {showAddressOrderId === order.id && (
                      <div className="address-dropdown">
                        <div className="address-dropdown-content">
                          <p><strong>{order.shippingAddress?.fullName}</strong></p>
                          <p>{order.shippingAddress?.addressLine1}</p>
                          {order.shippingAddress?.addressLine2 && <p>{order.shippingAddress.addressLine2}</p>}
                          <p>{order.shippingAddress?.city}, {order.shippingAddress?.state} {order.shippingAddress?.pincode}</p>
                          <p>Phone: {order.shippingAddress?.phone}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Quick Delivery Badge - as 4th column */}
                  {order.deliveryType === 'quick' && (
                    <div className="header-column quick-column">
                      <span className={`quick-delivery-tag ${order.status === 'delivered' ? 'delivered' : order.tracking?.isLive ? 'active' : 'pending'}`}>
                        Quick Delivery
                      </span>
                      <span className="quick-status-text">
                        {order.status === 'delivered' ? 'Delivered' :
                          order.tracking?.isLive ? 'Track Live' :
                            'Finding rider...'}
                      </span>
                    </div>
                  )}
                </div>
                <div className="order-header-right">
                  <div className="order-id-row">
                    <span className="order-id-label">ORDER #</span>
                    <span className="order-id-value">{order.orderId}</span>
                  </div>
                  <div className="header-links">
                    <button className="header-link" onClick={() => toggleOrderDetails(order.id)}>
                      View order details
                    </button>
                  </div>
                </div>
              </div>

              {/* Expanded Order Details */}
              {expandedOrderId === order.id && (
                <div className="order-details-expanded">
                  <div className="details-grid">
                    <div className="details-section">
                      <h4>Shipping Address</h4>
                      {order.shippingAddress && (
                        <>
                          <p>{order.shippingAddress.fullName}</p>
                          <p>{order.shippingAddress.addressLine1}</p>
                          {order.shippingAddress.addressLine2 && <p>{order.shippingAddress.addressLine2}</p>}
                          <p>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.pincode}</p>
                          <p>Phone: {order.shippingAddress.phone}</p>
                        </>
                      )}
                    </div>
                    <div className="details-section">
                      <h4>Payment</h4>
                      <p>Method: {order.paymentMethod || 'Online Payment'}</p>
                      <p>Total: ₹{order.totalPrice?.toLocaleString('en-IN')}</p>
                    </div>
                    {order.shippingDetails?.courier_name && (
                      <div className="details-section">
                        <h4>Shipping</h4>
                        <p>Courier: {order.shippingDetails.courier_name}</p>
                        {order.shippingDetails.awb_code && <p>AWB: {order.shippingDetails.awb_code}</p>}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Order Body */}
              <div className="order-body">
                <div className="order-body-left">
                  {/* Status */}
                  <div className="order-status-section">
                    <h3 className={`status-title ${statusInfo.className}`}>{statusInfo.title}</h3>
                    <p className="status-subtitle">{statusInfo.subtitle}</p>
                  </div>

                  {/* Items */}
                  <div className="order-items-compact">
                    {order.items?.map((item, index) => (
                      <div key={index} className="order-item-compact">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="item-image-compact"
                          onClick={() => handleProductClick(item)}
                        />
                        <div className="item-info-compact">
                          <h4
                            className="item-name-compact"
                            onClick={() => handleProductClick(item)}
                          >
                            {item.name}
                          </h4>
                          <div className="item-actions-compact">
                            <button
                              className="btn-amazon btn-amazon-primary"
                              onClick={() => handleBuyAgain(item)}
                            >
                              Buy it again
                            </button>
                            <button
                              className="btn-amazon btn-amazon-secondary"
                              onClick={() => handleProductClick(item)}
                            >
                              View your item
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right Side Action Buttons */}
                <div className="order-body-right">
                  <div className="action-btn-stack">
                    {/* Cancel Order - only for pending/processing orders, hide while cancelling */}
                    {canCancelOrder(order) && !cancellingOrderIds.has(order.id) && (
                      <button
                        className="btn-amazon btn-amazon-secondary btn-cancel"
                        onClick={() => handleCancelOrder(order.id)}
                      >
                        Cancel Order
                      </button>
                    )}

                    {/* Show cancelling state */}
                    {cancellingOrderIds.has(order.id) && (
                      <button
                        className="btn-amazon btn-amazon-secondary btn-cancel"
                        disabled
                        style={{ opacity: 0.7, cursor: 'not-allowed' }}
                      >
                        Cancelling...
                      </button>
                    )}

                    {/* Return - only for delivered orders */}
                    {order.status === 'delivered' && (
                      <button
                        className="btn-amazon btn-amazon-secondary"
                        onClick={() => handleReturnClick(order)}
                      >
                        Return
                      </button>
                    )}

                    {/* Cancellation Pending indicator */}
                    {order.status === 'cancellation_pending' && (
                      <button
                        className="btn-amazon btn-amazon-secondary"
                        disabled
                        style={{ background: '#ff9800', color: 'white', opacity: 0.9 }}
                      >
                        Awaiting Confirmation
                      </button>
                    )}

                    {/* Refund status - for cancelled/cancellation_pending orders with refund */}
                    {(order.status === 'cancelled' || order.status === 'cancellation_pending') && (order.refundDetails?.refundId || order.refundDetails?.success) && (
                      <button
                        className="btn-amazon btn-amazon-secondary"
                        onClick={() => handleRefundStatus(order)}
                      >
                        Refund Status
                      </button>
                    )}

                    {/* Request Refund - for cancelled orders WITHOUT refund (COD orders etc) */}
                    {order.status === 'cancelled' && !order.refundDetails?.refundId && !order.refundDetails?.success && (
                      <button
                        className="btn-amazon btn-amazon-secondary"
                        onClick={() => handleGetSupport(order)}
                      >
                        Request Refund
                      </button>
                    )}

                    {/* View Return Status */}
                    {['return_requested', 'return_approved', 'return_pickup_confirmed'].includes(order.status) && (
                      <div style={{
                        background: order.status === 'return_pickup_confirmed' ? '#d4edda' : '#fff3cd',
                        border: `1px solid ${order.status === 'return_pickup_confirmed' ? '#28a745' : '#ffc107'}`,
                        borderRadius: '8px',
                        padding: '12px',
                        marginBottom: '10px',
                        fontSize: '0.85rem'
                      }}>
                        <strong>
                          {order.status === 'return_pickup_confirmed' ? '✅ ' : '🔄 '}
                          Return Status:
                        </strong>
                        <p style={{ margin: '5px 0 0 0' }}>
                          {order.returnDetails?.status === 'pending_admin_approval' &&
                            'Your return request is being reviewed by our team.'}
                          {order.returnDetails?.status === 'pending_pickup' &&
                            'Return approved! Courier will contact you for pickup. Refund will be processed after pickup.'}
                          {order.status === 'return_pickup_confirmed' &&
                            'Product picked up! Your refund is being processed.'}
                        </p>
                        {order.returnDetails?.refundBreakdown && (
                          <p style={{ margin: '5px 0 0 0', fontWeight: 'bold', color: '#28a745' }}>
                            Expected Refund: ₹{order.returnDetails.refundBreakdown.refundAmount}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Get Support - always visible */}
                    <button
                      className="btn-amazon btn-amazon-secondary"
                      onClick={() => handleGetSupport(order)}
                    >
                      Get Support
                    </button>

                    {/* Track Order - show for non-Quick orders OR delivered Quick orders */}
                    {(order.deliveryType !== 'quick' || order.status === 'delivered' || order.status === 'cancelled' || !order.tracking?.isLive) && (
                      <button
                        className="btn-amazon btn-amazon-primary"
                        onClick={() => handleTrackOrder(order)}
                      >
                        Track Order
                      </button>
                    )}

                    {/* Track Live - show for Quick orders when live tracking is active */}
                    {order.deliveryType === 'quick' && order.tracking?.isLive && ['pending', 'processing', 'shipped'].includes(order.status) && (
                      <button
                        className="btn-amazon btn-amazon-primary"
                        style={{
                          background: 'linear-gradient(135deg, #00b894 0%, #00a085 100%)',
                          borderColor: '#00a085'
                        }}
                        onClick={() => {
                          // Navigate to in-app tracking page instead of opening new tab
                          navigate(`/track/${order._id || order.id}`);
                        }}
                      >
                        Track Live
                      </button>
                    )}

                    {/* Write a product review - for delivered orders */}
                    {order.status === 'delivered' && order.items?.length > 0 && (
                      <button
                        className="btn-amazon btn-amazon-secondary"
                        onClick={() => handleWriteReview(order.items[0])}
                      >
                        Write review
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Return Modal */}
      {showReturnModal && (
        <div className="modal-overlay">
          <div className="modal-content return-modal">
            <div className="modal-header">
              <h2>Request Return / Exchange</h2>
              <button onClick={() => setShowReturnModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <FiX size={24} />
              </button>
            </div>

            {/* Important Notice */}
            <div style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              fontSize: '0.9rem'
            }}>
              <strong>📢 Return Policy Notice:</strong>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                <li><strong>Full Refund (Damaged/Wrong Item):</strong> Requires video proof & admin approval. Refund after pickup confirmation.</li>
                <li><strong>Partial Refund (Other reasons):</strong> ₹{RETURN_SHIPPING_CHARGE} shipping deducted. No admin approval needed. Refund after pickup.</li>
              </ul>
            </div>

            <form onSubmit={handleSubmitReturn}>
              <div className="form-group">
                <label>I want to:</label>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', fontWeight: 'normal' }}>
                    <input
                      type="radio"
                      name="type"
                      value="return"
                      checked={returnType === 'return'}
                      onChange={(e) => setReturnType(e.target.value)}
                      style={{ marginRight: '8px', width: 'auto' }}
                    />
                    Return for Refund
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', fontWeight: 'normal' }}>
                    <input
                      type="radio"
                      name="type"
                      value="exchange"
                      checked={returnType === 'exchange'}
                      onChange={(e) => setReturnType(e.target.value)}
                      style={{ marginRight: '8px', width: 'auto' }}
                    />
                    Exchange Item
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Reason for Return:</label>
                <select value={returnReason} onChange={(e) => setReturnReason(e.target.value)} required>
                  <option value="">Select a reason</option>
                  <option value="damaged">📦 Item Damaged (Full Refund)</option>
                  <option value="wrong_item">❌ Wrong Item Received (Full Refund)</option>
                  <option value="size_issue">📏 Size/Fit Issue (Shipping deducted)</option>
                  <option value="quality_issue">⭐ Quality Not as Expected (Shipping deducted)</option>
                  <option value="changed_mind">🔄 Changed My Mind (Shipping deducted)</option>
                  <option value="other">📝 Other Reason (Shipping deducted)</option>
                </select>
              </div>

              {/* Refund Breakdown */}
              {refundBreakdown && returnType === 'return' && (
                <div style={{
                  background: refundBreakdown.isFullRefund ? '#d4edda' : '#f8f9fa',
                  border: `1px solid ${refundBreakdown.isFullRefund ? '#28a745' : '#dee2e6'}`,
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#333' }}>💰 Refund Breakdown</h4>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span>Order Total:</span>
                    <span>₹{refundBreakdown.orderTotal?.toFixed(2)}</span>
                  </div>
                  {!refundBreakdown.isFullRefund && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#dc3545' }}>
                      <span>Return Shipping Charge:</span>
                      <span>- ₹{refundBreakdown.shippingDeduction?.toFixed(2)}</span>
                    </div>
                  )}
                  <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #ddd' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    <span>Refund Amount:</span>
                    <span style={{ color: '#28a745' }}>₹{refundBreakdown.refundAmount?.toFixed(2)}</span>
                  </div>
                  <p style={{ margin: '8px 0 0 0', fontSize: '0.85rem', color: refundBreakdown.isFullRefund ? '#28a745' : '#666' }}>
                    {refundBreakdown.isFullRefund ? '✅ ' : 'ℹ️ '}{refundBreakdown.message}
                  </p>
                </div>
              )}

              {/* Video Upload - Only for damaged/wrong item */}
              {requiresVideoProof(returnReason) && (
                <div className="form-group">
                  <label style={{ color: '#dc3545', fontWeight: 'bold' }}>
                    📹 Upload Unboxing Video (Required for full refund):
                  </label>
                  <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '8px' }}>
                    Please upload a video showing the damaged/wrong item. This helps us process your full refund quickly.
                  </p>
                  <div
                    className="file-upload"
                    onClick={() => document.getElementById('video-upload').click()}
                    style={{
                      border: videoFile ? '2px solid #28a745' : '2px dashed #dc3545',
                      background: videoFile ? '#d4edda' : '#fff'
                    }}
                  >
                    {videoFile ? (
                      <span style={{ color: '#28a745' }}>✅ {videoFile.name}</span>
                    ) : (
                      <span style={{ color: '#dc3545' }}>Click to upload video (Max 50MB) - Required</span>
                    )}
                    <input
                      id="video-upload"
                      type="file"
                      accept="video/*"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>
              )}

              {/* No video required notice */}
              {returnReason && !requiresVideoProof(returnReason) && (
                <div style={{
                  background: '#e7f3ff',
                  border: '1px solid #0066cc',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  fontSize: '0.9rem'
                }}>
                  <p style={{ margin: 0 }}>
                    ℹ️ Video proof is not required for this return reason. However, ₹{RETURN_SHIPPING_CHARGE} return shipping charge will be deducted from your refund.
                  </p>
                </div>
              )}

              <button
                type="submit"
                className="submit-btn"
                disabled={uploading || (requiresVideoProof(returnReason) && !videoFile)}
                style={{
                  opacity: (uploading || (requiresVideoProof(returnReason) && !videoFile)) ? 0.6 : 1
                }}
              >
                {uploading ? 'Submitting...' : 'Submit Return Request'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Tracking Modal */}
      {showTrackingModal && (
        <div className="modal-overlay">
          <div className="modal-content tracking-modal">
            <div className="modal-header">
              <h2>Track Your Order</h2>
              <button onClick={() => setShowTrackingModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <FiX size={24} />
              </button>
            </div>

            {trackingLoading ? (
              <div className="tracking-loading">
                <FiPackage size={48} className="spinning" />
                <p>Loading tracking information...</p>
              </div>
            ) : trackingData ? (
              <div className="tracking-content">
                <div className="tracking-order-info">
                  <p><strong>Order ID:</strong> {trackingData.order?.orderId}</p>
                  {trackingData.order?.shippingDetails?.awb_code && (
                    <p><strong>AWB:</strong> {trackingData.order.shippingDetails.awb_code}</p>
                  )}
                  {trackingData.order?.shippingDetails?.courier_name && (
                    <p><strong>Courier:</strong> {trackingData.order.shippingDetails.courier_name}</p>
                  )}
                  {trackingData.order?.status !== 'cancelled' && trackingData.order?.status !== 'delivered' && trackingData.expectedDelivery && (
                    <p className="expected-delivery">
                      <strong>Expected Delivery:</strong> {trackingData.expectedDelivery}
                    </p>
                  )}
                  {trackingData.order?.status === 'delivered' && (
                    <p className="delivered-date">
                      <strong>Delivered on:</strong> {new Date(trackingData.order?.deliveredAt || trackingData.order?.updatedAt).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </p>
                  )}
                </div>

                <div className="tracking-timeline">
                  {trackingData.timeline?.map((step, index) => (
                    <div
                      key={index}
                      className={`timeline-step ${step.completed ? 'completed' : ''} ${step.isCancelled ? 'cancelled' : ''}`}
                    >
                      <div className="timeline-marker">
                        <div className="marker-dot"></div>
                        {index < trackingData.timeline.length - 1 && <div className="marker-line"></div>}
                      </div>
                      <div className="timeline-content">
                        <h4>{step.title}</h4>
                        <p>{step.description}</p>
                        {step.date && (
                          <span className="timeline-date">
                            {new Date(step.date).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        )}
                        {step.awb && (
                          <span className="timeline-awb">AWB: {step.awb}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="tracking-error">
                <p>Unable to load tracking information. Please try again.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Refund Status Modal */}
      {showRefundModal && (
        <div className="modal-overlay">
          <div className="modal-content tracking-modal">
            <div className="modal-header">
              <h2>Refund Status</h2>
              <button onClick={() => setShowRefundModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <FiX size={24} />
              </button>
            </div>

            {refundTrackingLoading ? (
              <div className="tracking-loading">
                <FiRefreshCw size={48} className="spinning" />
                <p>Fetching refund status from Razorpay...</p>
              </div>
            ) : refundTrackingData ? (
              <div className="tracking-content">
                {/* Timeline */}
                <div className="tracking-timeline">
                  {refundTrackingData.timeline?.map((step, index) => (
                    <div
                      key={index}
                      className={`timeline-step ${step.completed ? 'completed' : ''}`}
                    >
                      <div className="timeline-marker">
                        <div className="marker-dot"></div>
                        {index < refundTrackingData.timeline.length - 1 && (
                          <div className="marker-line"></div>
                        )}
                      </div>
                      <div className="timeline-content">
                        <h4>{step.title}</h4>
                        <p>{step.description}</p>
                        {step.date && (
                          <span className="timeline-date">
                            {new Date(step.date).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="tracking-error">
                <p>Unable to load refund information. Please try again.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
